import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import theme from '../theme';

const OnboardingWizard = ({ steps, currentStep, onNext, onBack, onComplete, title, subtitle, isSubmitting = false }) => {
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.accent.teal}10)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: theme.fonts.ui
    }}>
      <div style={{
        maxWidth: '800px',
        width: '100%',
        background: theme.colors.bg.card,
        borderRadius: theme.radius.card,
        boxShadow: theme.colors.shadows.xl,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '32px',
          borderBottom: `1px solid ${theme.colors.borders}`,
          textAlign: 'center'
        }}>
          <h1 style={{
            fontFamily: theme.fonts.heading,
            fontSize: '32px',
            fontWeight: '700',
            color: theme.colors.text.primary,
            marginBottom: '8px'
          }}>
            {title}
          </h1>
          {subtitle && (
            <p style={{
              fontSize: '16px',
              color: theme.colors.text.secondary
            }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div style={{
          padding: '24px 32px',
          background: theme.colors.bg.secondary
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            {steps.map((step, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                flex: index < steps.length - 1 ? 1 : 'initial'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: index <= currentStep ? theme.colors.primary : theme.colors.bg.tertiary,
                  color: index <= currentStep ? '#FFF' : theme.colors.text.muted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.3s'
                }}>
                  {index < currentStep ? <Check size={16} /> : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div style={{
                    flex: 1,
                    height: '2px',
                    background: index < currentStep ? theme.colors.primary : theme.colors.borders,
                    margin: '0 8px',
                    transition: 'all 0.3s'
                  }} />
                )}
              </div>
            ))}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: theme.colors.text.muted
          }}>
            {steps.map((step, index) => (
              <div key={index} style={{
                fontWeight: index === currentStep ? '600' : '400',
                color: index === currentStep ? theme.colors.primary : theme.colors.text.muted
              }}>
                {step.label}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{
          padding: '32px',
          minHeight: '400px'
        }}>
          {steps[currentStep]?.content}
        </div>

        {/* Footer */}
        <div style={{
          padding: '24px 32px',
          borderTop: `1px solid ${theme.colors.borders}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onBack}
            disabled={currentStep === 0}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.cta,
              color: theme.colors.text.secondary,
              fontSize: '15px',
              fontWeight: '600',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              opacity: currentStep === 0 ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <ChevronLeft size={18} />
            Back
          </button>

          <div style={{
            fontSize: '14px',
            color: theme.colors.text.muted
          }}>
            Step {currentStep + 1} of {steps.length}
          </div>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={onNext}
              style={{
                padding: '12px 24px',
                background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent.teal})`,
                border: 'none',
                borderRadius: theme.radius.cta,
                color: '#FFF',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: theme.colors.shadows.md,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = theme.colors.shadows.lg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = theme.colors.shadows.md;
              }}
            >
              Next
              <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={onComplete}
              disabled={isSubmitting}
              style={{
                padding: '12px 32px',
                background: isSubmitting ? '#ccc' : `linear-gradient(135deg, ${theme.colors.accent.teal}, ${theme.colors.primary})`,
                border: 'none',
                borderRadius: theme.radius.cta,
                color: '#FFF',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: theme.colors.shadows.md,
                transition: 'all 0.2s',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = theme.colors.shadows.lg;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.colors.shadows.md;
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner" style={{ 
                    width: '18px', 
                    height: '18px', 
                    border: '2px solid #FFF',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Complete Onboarding
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;